
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { TableItemFormatter } from '../../elements/charts/helpers/tableItemFormatter';
import {getTableName, getSqlColumnRef, isComputedProperty} from '@/worker/db/sqlHelpers';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import settingx from '@/store/modules/settingx';
import moment from 'moment';
import {PermissionsGroup} from '@/store/models.def';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import {addComma} from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';

@Component({
	components: {
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		IncludeTaxToggle,
		SlPhModuleOnlyToggle,
		TableCollapseToggle,
		TableFilterable,
	},
})
export default class DashboardSalesAgentRankingTable extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateAsOf'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];
    public periodTotalData: any[] = [];
    public periodTotalFields: any[] = [];

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');
		return 'Sales Agent Ranking' + '_' + formattedDate;
	}

	public get dateFormatted() {
		return this.selectedAsOfDate;
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

    public get numDecimal() {
        return gdbx.numDecimal;
    }

	public get expensiveHook() {
		const { includeTax, selectedAsOfDate, selectedAgents, includeDebitNote, includeCreditNote, slPhModuleOnly } = this;
		return JSON.stringify([
			includeTax,
			selectedAsOfDate,
			selectedAgents,
			includeDebitNote,
			includeCreditNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const dayStart = moment(this.selectedAsOfDate).startOf('day').valueOf();
		const dayEnd = moment(this.selectedAsOfDate).endOf('day').valueOf();

		const amountKey = this.slPhModuleOnly
			? (this.includeTax ? 'amountWithTax' : 'amount')
			: (this.includeTax ? 'arAmountWithTax' : 'arAmount');
		const profitKey = this.slPhModuleOnly
			? (this.includeTax ? 'profitIncludeTax' : 'profit')
			: (this.includeTax ? 'arProfitIncludeTax' : 'arProfit');

		const amountCol = getSqlColumnRef('dashboardSales', amountKey);
		const amountExpr = `TRY_CAST(${amountCol} AS DOUBLE)`;
		const profitRef = getSqlColumnRef('dashboardSales', profitKey);
		const profitExpr = isComputedProperty(profitKey) ? profitRef : `TRY_CAST(${profitRef} AS DOUBLE)`;
		const table = getTableName('dashboardSales');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['dashboardSales']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const rows = await queryHead.rawQuery(`
			SELECT "AGENT",
				SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${amountExpr} ELSE 0 END) as sales_amt,
				SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN ${profitExpr} ELSE 0 END) as sales_profit,
				SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN TRY_CAST("COST" AS DOUBLE) ELSE 0 END) as sales_cost,
				SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${amountExpr} ELSE 0 END) as dn_amt,
				SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${profitExpr} ELSE 0 END) as dn_profit,
				SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("COST" AS DOUBLE) ELSE 0 END) as dn_cost,
				SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${amountExpr} ELSE 0 END) as cn_amt,
				SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${profitExpr} ELSE 0 END) as cn_profit,
				SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("COST" AS DOUBLE) ELSE 0 END) as cn_cost
			FROM ${table}
			WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${dayStart} AND ${dayEnd}
			GROUP BY "AGENT"
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		this.tableFields = [
			{ key: 'agent_name', sortable: true, stickyColumn: true },
			{ key: 'total', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'cost', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'profit', sortable: true, formatter: TableItemFormatter.currencySplit },
			{ key: 'profit_margin', sortable: true, formatter: TableItemFormatter.percentage },
		];

		let totalSales = 0;
		let totalProfit = 0;
		let totalCost = 0;

		const result = rows.map((row) => {
			const salesAmt = Number(row.sales_amt) || 0;
			const salesProfit = Number(row.sales_profit) || 0;
			const salesCost = Number(row.sales_cost) || 0;
			const dnAmt = Number(row.dn_amt) || 0;
			const dnProfit = Number(row.dn_profit) || 0;
			const dnCost = Number(row.dn_cost) || 0;
			const cnAmt = Number(row.cn_amt) || 0;
			const cnProfit = Number(row.cn_profit) || 0;
			const cnCost = Number(row.cn_cost) || 0;

			const total = salesAmt
				+ (this.includeDebitNote ? dnAmt : 0)
				- (this.includeCreditNote ? cnAmt : 0);
			const profit = salesProfit
				+ (this.includeDebitNote ? dnProfit : 0)
				- (this.includeCreditNote ? cnProfit : 0);
			const cost = salesCost
				+ (this.includeDebitNote ? dnCost : 0)
				- (this.includeCreditNote ? cnCost : 0);

			totalSales += total;
			totalProfit += profit;
			totalCost += cost;

			return {
				agent_name: gdbx.agentNames[row.AGENT] || row.AGENT,
				total,
				cost,
				profit,
				profit_margin: total !== 0 ? (profit / total) * 100 : 0,
			};
		});

		this.periodTotalFields = [
			{ key: 'total_sales', label: 'Total Sales', textAlign: 'center' },
			{ key: 'total_cost', label: 'Total Cost', textAlign: 'center' },
			{ key: 'total_profit', label: 'Total Sales Profit', textAlign: 'center' },
			{ key: 'total_profit_margin', label: 'Total Profit Margin', formatter: TableItemFormatter.percentage, textAlign: 'center' },
		];

		this.periodTotalData = [
			{
				total_sales: this.currencySymbol + ' ' + addComma(totalSales, this.numDecimal, true),
				total_cost: this.currencySymbol + ' ' + addComma(totalCost, this.numDecimal, true),
				total_profit: this.currencySymbol + ' ' + addComma(totalProfit, this.numDecimal, true),
				total_profit_margin: totalSales !== 0 ? ((totalProfit / totalSales) * 100) : 0,
			},
		];

		this.tableItems = result.filter((item) => item.total !== 0);

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields', 'periodTotalData', 'periodTotalFields');
	}
}
